import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { formatToolCallInstruction } from "./tool-call-formatting-ALc98KqF.js";
import { fetchSnapshotSummary } from "./handlers-CCIzlp0E.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-snapshot.ts
var get_snapshot_default = defineTool({
	name: "get_snapshot",
	skills: ["inspect"],
	requiredScopes: ["project:read"],
	description: ({ experimentalMode, availableToolNames, directToolNames }) => {
		const imageInstruction = formatToolCallInstruction({
			toolName: "get_snapshot_image",
			arguments: {
				organizationSlug: "<organization_slug>",
				snapshotId: "<snapshot_id>",
				imageIdentifier: "<image_file_name>"
			},
			experimentalMode,
			availableToolNames,
			directToolNames,
			fallbackInstruction: "Use the Sentry tool `get_sentry_resource`"
		});
		const imageInstructionSuffix = imageInstruction.includes("get_snapshot_image") ? " to view a specific image preview or full-resolution image bytes." : " to view a specific image preview.";
		return [
			"Get a preprod snapshot comparison summary, including metadata, counts, and changed image sections.",
			"",
			"Use this tool when you need to:",
			"- Investigate a failed snapshot test from CI",
			"- Review what changed in a specific preprod snapshot",
			"- Browse snapshot image file names before viewing a specific image",
			"",
			"Pass organizationSlug and snapshotId. Use get_sentry_resource for snapshot URLs.",
			"Compact output is returned by default. Set showUnmodified=true to list unchanged and skipped images separately.",
			"",
			"<examples>",
			"### Browse a snapshot",
			"",
			"```",
			"get_snapshot(organizationSlug=\"sentry\", snapshotId=\"231949\")",
			"```",
			"",
			"### Include unchanged and skipped images",
			"",
			"```",
			"get_snapshot(organizationSlug=\"sentry\", snapshotId=\"231949\", showUnmodified=true)",
			"```",
			"</examples>",
			"",
			"<hints>",
			`- ${imageInstruction}${imageInstructionSuffix}`,
			"- Use get_sentry_resource when starting from a Sentry snapshot URL.",
			"- The diff percent field shows what percentage of pixels changed (0-100).",
			"- showUnmodified=true is useful when a diff snapshot has no changed image sections.",
			"</hints>"
		].join("\n");
	},
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		snapshotId: z.string().trim().min(1).describe("The numeric snapshot artifact ID."),
		showUnmodified: z.boolean().describe("When true, include unchanged and skipped image sections. This can substantially increase response size and token usage for large snapshots.").default(false),
		regionUrl: ParamRegionUrl.nullable().default(null)
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		if (!params.organizationSlug || !params.snapshotId) throw new UserInputError("Provide both organizationSlug and snapshotId. Use get_sentry_resource for snapshot URLs.");
		setTag("organization.slug", params.organizationSlug);
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		return fetchSnapshotSummary(apiService, params.organizationSlug, params.snapshotId, null, {
			showUnmodified: params.showUnmodified,
			listImagesWhenNoDiffs: true,
			experimentalMode: context.experimentalMode ?? false,
			availableToolNames: context.availableToolNames,
			directToolNames: context.directToolNames
		});
	}
});

//#endregion
export { get_snapshot_default };
//# sourceMappingURL=get-snapshot-Ce1Z98WR.js.map